import React, { useState, useEffect, useMemo } from 'react';
import { TextField, TextFieldProps } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { FetchError } from '../../../common/api/api';
import SearchFilterButton from '../../../common/SearchFilterButton';
import SearchFilterDrawer, {
  BaseSearchFilterProps
} from '../../../common/SearchFilterDrawer';
import { InlineFormControl } from '../../../common/inline/InlineFormControl';
import {
  useGetJiraIssueTypes,
  useGetJiraProjects,
  useGetJiraStatuses
} from '../JiraConnect.hooks';
import { JiraProject } from '../JiraConnect.interface';

export interface JiraListFilterOptions {
  status?: string;
  project?: string;
  issueType?: string;
}

function createInitialOptions(): JiraListFilterOptions {
  return {};
}

function mapIdName(item: { id: string; name: string }): SelectFilterViewOption {
  return { value: item.id, label: item.name };
}

export type JiraListFilterProps = BaseSearchFilterProps<JiraListFilterOptions>;

const JiraListFilter: React.FC<JiraListFilterProps> = (
  props: JiraListFilterProps
) => {
  const { onFilter } = props;
  const [open, setOpen] = useState<boolean>(false);
  const [statuses, setStatuses] = useState<SelectFilterViewOption[]>([]);
  const [projects, setProjects] = useState<SelectFilterViewOption[]>([]);
  const [issueTypes, setIssueTypes] = useState<SelectFilterViewOption[]>([]);
  const [filter, setFilter] = useState<JiraListFilterOptions>(
    createInitialOptions()
  );
  const [
    { data: statusList, ...statusesResult },
    getStatuses
  ] = useGetJiraStatuses();
  const [
    { data: projectList, ...projectsResult },
    getProjects
  ] = useGetJiraProjects();
  const [
    { data: issueTypeList, ...issueTypesResult },
    getIssueTypes
  ] = useGetJiraIssueTypes();

  useEffect(() => {
    getStatuses();
    getProjects();
    getIssueTypes();
  }, [getStatuses, getProjects, getIssueTypes]);

  useEffect(() => {
    if (statusList) {
      setStatuses(statusList.map(mapIdName));
    }
  }, [statusList]);

  useEffect(() => {
    if (projectList) {
      setProjects(
        projectList.map((project: JiraProject) => ({
          value: project.id,
          label: `${project.name} (${project.key})`
        }))
      );
    }
  }, [projectList]);

  useEffect(() => {
    if (issueTypeList) {
      setIssueTypes(issueTypeList.map(mapIdName));
    }
  }, [issueTypeList]);

  return (
    <>
      <SearchFilterButton onClick={() => setOpen(true)} />
      <SearchFilterDrawer
        open={open}
        title='Filter JIRA Issues'
        onClose={() => setOpen(false)}
        onClear={() => setFilter(createInitialOptions())}
        onFilter={() => {
          setOpen(false);
          onFilter?.(filter);
        }}
      >
        <SelectFilterView
          label='Project'
          options={projects}
          value={filter.project}
          error={projectsResult.error}
          isLoading={projectsResult.isLoading}
          textFieldProps={{ placeholder: 'Select Project' }}
          onChange={(project?: string) => setFilter({ ...filter, project })}
        />
        <SelectFilterView
          label='Issue Type'
          options={issueTypes}
          value={filter.issueType}
          error={issueTypesResult.error}
          isLoading={issueTypesResult.isLoading}
          textFieldProps={{ placeholder: 'Select Issue Type' }}
          onChange={(issueType?: string) => setFilter({ ...filter, issueType })}
        />
        <SelectFilterView
          label='Status'
          options={statuses}
          value={filter.status}
          error={statusesResult.error}
          isLoading={statusesResult.isLoading}
          textFieldProps={{ placeholder: 'Select Status' }}
          onChange={(status?: string) => setFilter({ ...filter, status })}
        />
      </SearchFilterDrawer>
    </>
  );
};

export default JiraListFilter;

interface SelectFilterViewOption {
  value: string;
  label: string;
}

interface SelectFilterViewProps {
  label: string;
  value?: string;
  error?: FetchError;
  isLoading?: boolean;
  options?: SelectFilterViewOption[];
  textFieldProps?: Omit<TextFieldProps, 'variant'>;
  onChange?: (value?: string) => void;
}

const SelectFilterView: React.FC<SelectFilterViewProps> = (
  props: SelectFilterViewProps
) => {
  const {
    value,
    label,
    error,
    isLoading,
    options,
    textFieldProps,
    onChange
  } = props;

  const selected: SelectFilterViewOption | undefined = useMemo(() => {
    return value && options
      ? options.find((option: SelectFilterViewOption) => option.value === value)
      : undefined;
  }, [value, options]);

  return (
    <InlineFormControl label={label}>
      <Autocomplete
        value={selected || null}
        options={options || []}
        getOptionLabel={(option: SelectFilterViewOption) => option.label}
        getOptionSelected={(
          option: SelectFilterViewOption,
          value: SelectFilterViewOption | null
        ) => option.value === value?.value}
        onChange={(
          _: React.ChangeEvent<{}>,
          value: SelectFilterViewOption | null
        ) => {
          onChange?.(value?.value || undefined);
        }}
        renderInput={params => (
          <TextField
            {...params}
            fullWidth
            variant='outlined'
            label={
              isLoading ? 'Loading...' : error ? 'An error occurred' : undefined
            }
            {...textFieldProps}
          />
        )}
      />
    </InlineFormControl>
  );
};
